import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { PHONE_MODELS, defaultKeys, describeModel, formatKeys, formatMac, parseKeys, phoneModel, type PhoneKey } from '@stitch/pbx-adapter/phones';
import { lookup } from '@stitch/pbx-adapter/describe';
import { api, type PhoneTemplate } from '../../api/client';
import { PhoneLayout } from '../../components/PhoneLayout';
import { ReviewDrawer } from '../../components/ReviewDrawer';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { plural, shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';
import { KeysWorkbench, modelLabel, namer } from './Phones';

/** Saved key layouts, to set up many phones the same way. */
export function TenantTemplates() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const { state } = useAuth();
  const navigate = useNavigate();
  const data = useData(() => api.templates(d), [d]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);
  const [creating, setCreating] = useState(false);

  const behind = (id: string) => data.data?.uses.filter((u) => u.templateId === id && !u.current).length ?? 0;

  return (
    <>
      <PageHeader
        eyebrow={data.data ? plural(data.data.templates.length, 'template') : d}
        title="Phone templates"
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            New template
          </Button>
        }
      />
      <div className="content">
        <Notice title="Set up phones the same way, in one go">
          A template is a key layout for a phone model: park keys, the front desk, the team’s queues. Put it on any number of phones, and see which ones are behind when it changes.
          Each person’s own line stays theirs.
        </Notice>
        {data.error ? (
          <Card>
            <LoadError error={data.error} retry={data.reload} />
          </Card>
        ) : !data.data ? (
          <Loading label="Loading templates" />
        ) : data.data.templates.length === 0 ? (
          <Card>
            <Empty title="No templates yet">Start one here, or open a phone whose keys you like and choose Save as template.</Empty>
          </Card>
        ) : (
          <div className="tpl-grid">
            {data.data.templates.map((t) => {
              const m = phoneModel(t.model);
              const keys = parseKeys(formatKeys(t.keys));
              return (
                <Link key={t.id} to={`/t/${encodeURIComponent(d)}/templates/${t.id}`} className="tpl-card">
                  <div className="tpl-preview" aria-hidden="true">
                    {m && <PhoneLayout model={m} keys={keys} nameOf={namer(dir.data)} />}
                  </div>
                  <div className="tpl-body">
                    <strong>{t.name}</strong>
                    <span className="muted">
                      {modelLabel(t.model)} · {plural(keys.length, 'key')} set
                    </span>
                    <span className="tpl-chips">
                      {t.shared && (
                        <Chip small tone="brand">
                          From Stitch
                        </Chip>
                      )}
                      <Chip small>{t.uses ? `On ${plural(t.uses, 'phone')}` : 'Not used yet'}</Chip>
                      {behind(t.id) > 0 && (
                        <Chip small tone="warn">
                          {behind(t.id)} behind
                        </Chip>
                      )}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      <NewTemplate
        open={creating}
        staff={Boolean(state?.user?.staff)}
        onClose={() => setCreating(false)}
        onCreate={async (name, model, shared) => {
          const { template } = await api.createTemplate(d, { name, model, keys: formatKeys(defaultKeys(phoneModel(model))), ...(shared ? { shared } : {}) });
          navigate(`/t/${encodeURIComponent(d)}/templates/${template.id}`);
        }}
      />
    </>
  );
}

function NewTemplate({
  open,
  staff,
  onClose,
  onCreate
}: {
  open: boolean;
  staff: boolean;
  onClose: () => void;
  onCreate: (name: string, model: string, shared: boolean) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [model, setModel] = useState('yealink-t54w');
  const [shared, setShared] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New phone template"
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await onCreate(name.trim(), model, shared);
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
                setBusy(false);
              }
            }}
          >
            Create and set the keys
          </Button>
        </>
      }
    >
      <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Front desk, Sales team…" maxLength={60} data-autofocus />
      <div className="field">
        <label htmlFor="tpl-model">Phone model</label>
        <select id="tpl-model" className="input" value={model} onChange={(e) => setModel(e.target.value)}>
          {PHONE_MODELS.filter((m) => m.lineKeys > 0).map((m) => (
            <option key={m.id} value={m.id}>
              {m.vendor} {m.model}
            </option>
          ))}
        </select>
        <span className="hint">{describeModel(phoneModel(model)!)}</span>
      </div>
      {staff && (
        <label className="check">
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
          Offer it to every customer (a Stitch template)
        </label>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

/** One template: its keys on the phone, saving them, and putting them on phones. */
export function TemplateEditor() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state } = useAuth();
  const data = useData(() => api.templates(d), [d]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);
  const phones = useData(() => api.accounts(d, 'phone').catch(() => null), [d]);
  const t = data.data?.templates.find((x) => x.id === id);
  const [keys, setKeys] = useState<PhoneKey[] | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [choosing, setChoosing] = useState(false);
  const [targets, setTargets] = useState<string[]>([]);
  const [reviewing, setReviewing] = useState(false);
  const [reviewKey, setReviewKey] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const origKeys = useMemo(() => parseKeys(formatKeys(t?.keys ?? [])), [t]);
  const k = keys ?? origKeys;
  const changed = useMemo(() => {
    const before = new Map(origKeys.map((x) => [x.key, JSON.stringify(x)]));
    const after = new Map(k.map((x) => [x.key, JSON.stringify(x)]));
    return new Set([...before.keys(), ...after.keys()].filter((key) => before.get(key) !== after.get(key)));
  }, [origKeys, k]);

  if (data.error) {
    return (
      <div className="content">
        <Card>
          <LoadError error={data.error} retry={data.reload} />
        </Card>
      </div>
    );
  }
  if (!data.data) return <Loading label="Loading the template" />;
  if (!t) {
    return (
      <div className="content">
        <Card>
          <Empty title="No such template">
            <Link to={`/t/${encodeURIComponent(d)}/templates`}>Back to templates</Link>
          </Empty>
        </Card>
      </div>
    );
  }
  const m = phoneModel(t.model);
  const canEdit = !t.shared || Boolean(state?.user?.staff);
  const dirty = changed.size > 0 || (name !== null && name.trim() !== t.name);
  const uses = data.data.uses.filter((u) => u.templateId === t.id);
  const behind = uses.filter((u) => !u.current).map((u) => u.phone);
  const all = phones.data?.accounts ?? [];
  const sameModel = all.filter((p) => p.values.model === t.model && p.values.user);

  async function save() {
    setBusy(true);
    setError('');
    try {
      await api.updateTemplate(d, t!.id, { ...(name !== null ? { name: name.trim() } : {}), ...(changed.size ? { keys: formatKeys(k) } : {}) });
      setKeys(null);
      setName(null);
      setSaved(behind.length || uses.length ? 'Saved. Phones using it change only when you put it on them again.' : 'Saved.');
      data.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.removeTemplate(d, t!.id);
      navigate(`/t/${encodeURIComponent(d)}/templates`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const ownerOf = (mac: string) => {
    const p = all.find((x) => x.extension === mac);
    return p?.values.user ? lookup(dir.data, p.values.user).name : 'Spare';
  };

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to={`/t/${encodeURIComponent(d)}/templates`}>Phone templates</Link> · {modelLabel(t.model)}
          </>
        }
        title={name ?? t.name}
        chips={t.shared ? <Chip tone="brand">From Stitch</Chip> : undefined}
        actions={
          <>
            {canEdit && (
              <Button variant="ghost" icon="trash" disabled={busy} onClick={() => setDeleting(true)}>
                Delete
              </Button>
            )}
            {canEdit && (
              <Button disabled={!dirty || busy} onClick={() => void save()}>
                Save template
              </Button>
            )}
            <Button
              variant="primary"
              disabled={dirty || !phones.data?.writable}
              title={dirty ? 'Save the template first' : undefined}
              onClick={() => {
                setTargets(behind.length ? behind : []);
                setChoosing(true);
              }}
            >
              Put it on phones
            </Button>
          </>
        }
      />
      <div className="content">
        {!canEdit && <Notice icon="lock">Stitch looks after this template. You can put it on your phones; to change it, save a phone’s keys as your own template.</Notice>}
        {saved && <Notice tone="ok">{saved}</Notice>}
        {error && <Notice tone="crit">{error}</Notice>}
        {behind.length > 0 && !dirty && (
          <Notice tone="warn" title={`${plural(behind.length, 'phone is', 'phones are')} behind`}>
            The template changed after they were set up from it. Choose Put it on phones to bring them up to date.
          </Notice>
        )}
        {m ? (
          <KeysWorkbench
            model={m}
            keys={k}
            setKeys={setKeys}
            dir={dir.data}
            owner="Each person’s own line"
            readOnly={!canEdit}
            changed={changed}
            side={
              <Card title="Template">
                <div className="stack card-body">
                  {canEdit && <Field label="Name" value={name ?? t.name} maxLength={60} onChange={(e) => setName(e.target.value)} />}
                  <dl className="facts">
                    <dt>Model</dt>
                    <dd>{modelLabel(t.model)}</dd>
                    <dt>Last changed</dt>
                    <dd>
                      {shortWhen(t.updatedAt)} by {t.updatedBy || 'Stitch'}
                    </dd>
                    <dt>On phones</dt>
                    <dd>{uses.length ? uses.map((u) => `${ownerOf(u.phone)}${u.current ? '' : ' (behind)'}`).join(', ') : 'None yet'}</dd>
                  </dl>
                </div>
              </Card>
            }
          />
        ) : (
          <Notice>Stitch can’t draw this model.</Notice>
        )}
      </div>

      <Dialog
        open={choosing}
        onClose={() => setChoosing(false)}
        title={`Put ${t.name} on phones`}
        subtitle={`${modelLabel(t.model)} phones that belong to someone. Each keeps its own line.`}
        foot={
          <>
            <Button variant="ghost" onClick={() => setChoosing(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={targets.length === 0}
              onClick={() => {
                setChoosing(false);
                setReviewKey((x) => x + 1);
                setReviewing(true);
              }}
            >
              Review {plural(targets.length, 'phone')}
            </Button>
          </>
        }
      >
        {sameModel.length === 0 ? (
          <Empty title={`No ${modelLabel(t.model)} phones with an owner`}>Add one on Desk phones first.</Empty>
        ) : (
          <div className="check-list">
            <label className="check">
              <input type="checkbox" checked={targets.length === sameModel.length} onChange={(e) => setTargets(e.target.checked ? sameModel.map((p) => p.extension) : [])} />
              <strong>All of them</strong>
            </label>
            {sameModel.map((p) => {
              const u = uses.find((x) => x.phone === p.extension);
              return (
                <label key={p.extension} className="check">
                  <input
                    type="checkbox"
                    checked={targets.includes(p.extension)}
                    onChange={(e) => setTargets((cur) => (e.target.checked ? [...cur, p.extension] : cur.filter((x) => x !== p.extension)))}
                  />
                  <span className="cell-stack">
                    <span>
                      {lookup(dir.data, p.values.user ?? '').name}{' '}
                      {u && (
                        <Chip small tone={u.current ? 'ok' : 'warn'}>
                          {u.current ? 'Up to date' : 'Behind'}
                        </Chip>
                      )}
                    </span>
                    <small className="mono muted">{formatMac(p.extension)}</small>
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </Dialog>

      {/* Deleting can't be undone, so it asks first (2985). Phones keep their keys. */}
      <Dialog
        open={deleting}
        onClose={() => setDeleting(false)}
        title={`Delete the ${t.name} template?`}
        foot={
          <>
            <Button variant="ghost" onClick={() => setDeleting(false)}>
              Cancel
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              {busy ? 'Deleting…' : 'Delete template'}
            </Button>
          </>
        }
      >
        <p>
          {t.uses
            ? `${t.uses === 1 ? 'One phone follows' : `${t.uses} phones follow`} it. ${t.uses === 1 ? 'It keeps its' : 'They keep their'} keys as they are, and stop following a template.`
            : 'No phone follows it.'}
        </p>
        <p className="muted">This can’t be undone.</p>
      </Dialog>
      <ReviewDrawer
        key={reviewKey}
        open={reviewing}
        tenant={d}
        items={targets.map((mac) => ({
          key: mac,
          description: `Put the ${t.name} keys on ${ownerOf(mac)}’s phone (${formatMac(mac)})`,
          intent: { op: 'account.update', kind: 'phone', extension: mac, set: { keys: formatKeys(k) } }
        }))}
        makePlan={async () => {
          const r = await api.planTemplate(d, t.id, targets);
          if (!r.changeSet) {
            data.reload();
            throw new Error(`${targets.length === 1 ? 'That phone already has' : 'Those phones already have'} these keys, so nothing needs to change. ${targets.length === 1 ? 'It now follows' : 'They now follow'} this template.`);
          }
          return r.changeSet;
        }}
        onUnstage={(key) => setTargets((cur) => cur.filter((x) => x !== key))}
        onClose={(applied) => {
          setReviewing(false);
          if (applied) {
            setTargets([]);
            data.reload();
          }
        }}
      />
    </>
  );
}

export type { PhoneTemplate };
